import { inject, injectable } from 'inversify-props';
import { IHttpClient, ILogger, ISettings } from '../common';
import { Series, DeckSeries, PagedResult, SeriesSearchRequest } from '../../models';

export interface ISeriesService {

	page(pageIndex: number, pageSize: number): Promise<PagedResult<Series>>;
	get(id: string): Promise<Series>;
	getMultiple(ids: string[]): Promise<Series[]>;
	autocomplete(crtieria: string): Promise<Series[]>;
	save(model: Series): Promise<Series>;
	pageByDeck(deckId: string, pageIndex: number, pageSize: number): Promise<PagedResult<DeckSeries>>;
	listByDeck(deckId: string): Promise<DeckSeries[]>;
	search(criteria: SeriesSearchRequest): Promise<PagedResult<Series>>;
	delete(id: string): Promise<any>;
}

@injectable()
export class SeriesService implements ISeriesService {

	constructor(
		@inject('IHttpClient') private httpClient: IHttpClient,
		@inject('ILogger') private logger: ILogger,
		@inject('ISettings') private settings: ISettings) {}


	public page(pageIndex: number, pageSize: number): Promise<PagedResult<Series>> {
		const url = `${this.settings.baseUrl}${this.settings.seriesBaseUrl}/${pageIndex}/${pageSize}`;
		return this.httpClient.get<PagedResult<Series>>(url);
	}

	public get(id: string): Promise<Series> {
		const url = `${this.settings.baseUrl}${this.settings.seriesBaseUrl}/${id}`;
		return this.httpClient.get<Series>(url);
	}

	public getMultiple(ids: string[]): Promise<Series[]> {
		const url = `${this.settings.baseUrl}${this.settings.seriesBaseUrl}/multiple/${ids.join(',')}`;
		return this.httpClient.get<Series[]>(url);
	}

	public autocomplete(criteria: string): Promise<Series[]> {
		const url = `${this.settings.baseUrl}${this.settings.seriesBaseUrl}/autocomplete/${criteria}`;
		return this.httpClient.get<Series[]>(url);
	}

	public save(model: Series): Promise<Series> {
		const payload: { [k: string]: any } = {
			id: model.id,
			name: model.name,
			description: model.description,
			year: (model.year != null) ? parseInt(model.year.toString(), null) : null,
			isEnabled: model.isEnabled,
		};
		if (model.companies && model.companies.length) { payload.companyIds = model.companies.map((x) => x.id); }
		let url = `${this.settings.baseUrl}${this.settings.seriesBaseUrl}`;
		if (model.id !== null && model.id !== undefined && model.id !== '') {
			url += `/${model.id}`;
			return this.httpClient.put(url, payload);
		} else {
			return this.httpClient.post(url, payload);
		}
	}

	public listByDeck(deckId: string): Promise<DeckSeries[]> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckId}/series`;
		return this.httpClient.get<DeckSeries[]>(url);
	}

	public pageByDeck(deckId: string, pageIndex: number, pageSize: number): Promise<PagedResult<DeckSeries>> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckId}/series/${pageIndex}/${pageSize}`;
		return this.httpClient.get<PagedResult<DeckSeries>>(url);
	}

	public search(request: SeriesSearchRequest): Promise<PagedResult<Series>> {
		const url = `${this.settings.baseUrl}${this.settings.seriesBaseUrl}/search`;
		return this.httpClient.post<PagedResult<Series>>(url, request);
	}

	public delete(id: string): Promise<any> {
		const url = `${this.settings.baseUrl}${this.settings.seriesBaseUrl}/${id}`;
		return this.httpClient.delete(url);
	}

}
